import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      await axios.post('/api/contact', formData);
      
      setSuccess(t('messageSent'));
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      setError(error.response?.data?.message || t('errorSendingMessage'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <EmailIcon color="primary" />,
      title: t('email'),
      content: 'Yogameliebenz@gmail.com',
      link: 'mailto:Yogameliebenz@gmail.com',
    },
    {
      icon: <PhoneIcon color="primary" />,
      title: t('phone'),
      content: '06 18 25 47 09',
      link: 'tel:+33618254709',
    },
    {
      icon: <LocationIcon color="primary" />,
      title: t('address'),
      content: '243 A Chemin des Baïsses, 84360 Mérindol',
      link: 'https://maps.google.com/?q=243+A+Chemin+des+Baïsses,+84360+Mérindol',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
      <Grid container spacing={{ xs: 4, sm: 5, md: 6 }}>
        {/* Contact Form Section */}
        <Grid item xs={12} md={7}>
          <Box sx={{ maxWidth: 600 }}>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{
                fontSize: {
                  xs: '1.75rem',
                  sm: '2.25rem',
                  md: '2.75rem'
                },
                fontWeight: 600,
                lineHeight: 1.3,
                mb: { xs: 1, sm: 2 }
              }}
            >
              {t('contactUs')}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              paragraph
              sx={{
                fontSize: {
                  xs: '1rem',
                  sm: '1.1rem',
                  md: '1.25rem'
                },
                lineHeight: 1.6,
                mb: { xs: 2, sm: 3 }
              }}
            >
              {t('contactDescription')}
            </Typography>

            {status.message && (
              <Alert 
                severity={status.type} 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {status.message}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                <TextField
                  fullWidth
                  label={t('name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('subject')}
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('message')}
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ 
                    width: 'fit-content',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 2, sm: 3 }
                  }}
                >
                  {loading ? t('sending') : t('sendMessage')}
                </Button>
              </Stack>
            </form>
          </Box>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12} md={5}>
          <Stack spacing={{ xs: 3, sm: 4 }}>
            {contactInfo.map((info, index) => (
              <Paper
                key={index}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: 'background.neutral',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                  transition: 'all 0.3s',
                }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                  <Box
                    sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1.5,
                      bgcolor: 'primary.lighter',
                    }}
                  >
                    {React.cloneElement(info.icon, {
                      sx: { fontSize: { xs: '1.25rem', sm: '1.5rem' } }
                    })}
                  </Box>
                  <Box>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {info.title}
                    </Typography>
                    <Link
                      href={info.link}
                      target={info.title === t('address') ? '_blank' : undefined}
                      rel={info.title === t('address') ? 'noopener noreferrer' : undefined}
                      color="text.secondary"
                      sx={{
                        textDecoration: 'none',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {info.content}
                    </Link>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Contact; 